import moment from 'moment'
import _ from 'lodash'
import { CULTURES_COUNTRY } from '@openflex/component'
import * as constants from '../../trade-in/defaults/VehicleAreas/constants'
import getCultureCountry from '../utils/cultures/country'

export const mapperMethods = {
  MAP_VEHICLE: 'map_vehicle',
  MAP_OPTIONS: 'map_option',
  MAP_ESTIMATED_DAMAGES: 'map_estimated_damages'
}

export const DAMAGE_TYPE = {
  MAINTENANCE: 1,
  PNEUMATIC: 2,
  DEFECT: 3,
  MANUAL: 4,
  SECOND_PNEUMATIC: 5
}

export const DESTINATION = {
  PRIVATE: 1,
  DEALER: 2
}

export const RECOVERABLE_VAT = {
  YES: 1,
  NO: 2,
  NO_INFORMATION: 4
}

const DEFAULT_STOCK_ENTRY_DELAY = 30

const getLargestTyreDiameter = vehicleInformations => {
  if (vehicleInformations.frontTyreDiameter || vehicleInformations.rearTyreDiameter) {
    if (
      Number(vehicleInformations.frontTyreDiameter) &&
      Number(vehicleInformations.rearTyreDiameter)
    ) {
      return String(
        _.max([
          Number(vehicleInformations.frontTyreDiameter),
          Number(vehicleInformations.rearTyreDiameter)
        ])
      )
    }
    return vehicleInformations.frontTyreDiameter || vehicleInformations.rearTyreDiameter
  }
  return 'Default'
}

const calculateForecastedMileage = (mileage, stockEntranceMaximumDays, putIntoService) => {
  let stockEntranceDays = stockEntranceMaximumDays
  if (stockEntranceMaximumDays === null) stockEntranceDays = DEFAULT_STOCK_ENTRY_DELAY

  const diffDaysPutIntoServiceFromNow = moment().diff(moment(putIntoService), 'days')
  const diffDaysNowFromForecastedDate = moment()
    .add(stockEntranceDays, 'd')
    .diff(moment(), 'days')
  const mileagePerDay = diffDaysPutIntoServiceFromNow
    ? mileage / diffDaysPutIntoServiceFromNow
    : mileage

  const forecastedMileage = (mileage + diffDaysNowFromForecastedDate * mileagePerDay).toFixed()

  return forecastedMileage > 999999 ? 999999 : forecastedMileage
}

class Mapper {
  static mapVehicle = datas => {
    const specificByCountry = () => {
      const country = getCultureCountry()
      if (country) {
        switch (country) {
          case CULTURES_COUNTRY.NO:
          case CULTURES_COUNTRY.SE:
          case CULTURES_COUNTRY.FI:
            return {
              secondFrontTyreWear: datas.secondTiresStateFront,
              secondRearTyreWear: datas.secondTiresStateBack,
              secondTyres: !!datas.secondTyres,
              tyresType: datas.tyresType && Number(datas.tyresType)
            }
          case CULTURES_COUNTRY.IT:
            return {
              recoverableVat: RECOVERABLE_VAT.NO_INFORMATION
            }
          case CULTURES_COUNTRY.FR:
          default:
            return {
              recoverableVat: Number(datas.financing) > 2 ? RECOVERABLE_VAT.YES : RECOVERABLE_VAT.NO
            }
        }
      }
      return {}
    }

    return {
      carId: Number(datas.id),
      configuration: datas.configuration,
      referentialCarId: datas.id,
      typeId: datas.wsDatas.typeId,
      type: datas.wsDatas.type,
      makeId: datas.wsDatas.makeId,
      make: datas.wsDatas.make,
      modelGroupId: datas.wsDatas.modelGroupId,
      modelGroup: datas.wsDatas.modelGroup,
      generation: datas.wsDatas.generation,
      modelId: datas.wsDatas.modelId,
      model: datas.wsDatas.model,
      trim: datas.wsDatas.trimline,
      version: datas.wsDatas.version,
      fuelId: datas.wsDatas.fuelId,
      fuel: datas.wsDatas.fuel,
      genericFuelId: datas.wsDatas.genericFuelId,
      genericFuel: datas.wsDatas.genericFuel,
      transmissionTypeId: datas.wsDatas.transmissionTypeId,
      transmissionType: datas.wsDatas.transmissionType,
      genericTransmissionTypeId: datas.wsDatas.genericTransmissionTypeId,
      genericTransmissionType: datas.wsDatas.genericTransmissionType,
      bodyId: datas.wsDatas.bodyId,
      body: datas.wsDatas.body,
      genericBodyId: datas.wsDatas.genericBodyId,
      genericBody: datas.wsDatas.genericBody,
      segmentationId: datas.wsDatas.segmentationId,
      segmentation: datas.wsDatas.segmentation,
      driveTypeId: datas.wsDatas.driveTypeId,
      driveType: datas.wsDatas.driveType,
      genericDriveTypeId: datas.wsDatas.genericDriveTypeId,
      genericDriveType: datas.wsDatas.genericDriveType,
      emissionId: datas.wsDatas.emissionId,
      emission: datas.wsDatas.emission,
      seats: datas.wsDatas.seats,
      gears: datas.wsDatas.gears,
      doors: datas.wsDatas.doors,
      valves: datas.wsDatas.valves,
      torque: datas.wsDatas.torque,
      displacement: datas.wsDatas.displacement,
      cylinders: datas.wsDatas.cylinders,
      cylindersTypeId: datas.wsDatas.cylindersTypeId,
      cylindersType: datas.wsDatas.cylindersType,
      horsepower: datas.wsDatas.horsepower,
      taxHorsepower: datas.wsDatas.taxHorsepower,
      kilowatt: datas.wsDatas.kilowatt,
      co2Emission: datas.wsDatas.co2Emission,
      wltpCo2EmissionMin: datas.wsDatas.wltpCo2EmissionMin,
      wltpCo2EmissionMax: datas.wsDatas.wltpCo2EmissionMax,
      mixedConsumption: datas.wsDatas.mixedConsumption,
      wltpMixedConsumptionMin: datas.wsDatas.wltpMixedConsumptionMin,
      wltpMixedConsumptionMax: datas.wsDatas.wltpMixedConsumptionMax,
      urbanConsumption: datas.wsDatas.urbanConsumption,
      wltpUrbanConsumptionMin: datas.wsDatas.wltpUrbanConsumptionMin,
      wltpUrbanConsumptionMax: datas.wsDatas.wltpUrbanConsumptionMax,
      extraUrbanConsumption: datas.wsDatas.extraUrbanConsumption,
      wltpExtraUrbanConsumptionMin: datas.wsDatas.wltpExtraUrbanConsumptionMin,
      wltpExtraUrbanConsumptionMax: datas.wsDatas.wltpExtraUrbanConsumptionMax,
      length: datas.wsDatas.length,
      width: datas.wsDatas.width,
      height: datas.wsDatas.height,
      totalWeight: datas.wsDatas.totalWeight,
      payload: datas.wsDatas.payload,
      wheelbase: datas.wsDatas.wheelbase,
      trailerLoadBraked: datas.wsDatas.trailerLoadBraked,
      trailerLoadUnbraked: datas.wsDatas.trailerLoadUnbraked,
      roofload: datas.wsDatas.roofload,
      curbWeight: datas.wsDatas.curbWeight || datas.curbWeight,
      topSpeed: datas.wsDatas.topSpeed,
      acceleration: datas.wsDatas.acceleration,
      trunkMinimumCapacity: datas.wsDatas.trunkMinimumCapacity,
      trunkMaximumCapacity: datas.wsDatas.trunkMaximumCapacity,
      modelCatalogBegin: datas.wsDatas.modelCatalogBegin,
      modelCatalogEnd: datas.wsDatas.modelCatalogEnd,
      catalogBeginPrice: datas.wsDatas.catalogPriceBegin,
      catalogEndPrice: datas.wsDatas.catalogPriceEnd,
      catalogExclVatPrice: datas.wsDatas.catalogExclVatPrice,
      catalogInclVatPrice: datas.wsDatas.catalogInclVatPrice,
      catalogTax: datas.wsDatas.catalogTax,
      catalogPriceOverloaded: false,
      frontTyreWidth: datas.wsDatas.frontTyreWidth,
      frontTyreHeight: datas.wsDatas.frontTyreHeight,
      frontTyreSpeedIndex: datas.wsDatas.frontTyreSpeedIndex,
      frontTyreDiameter: datas.wsDatas.frontTyreDiameter,
      frontTyreWear: datas.tiresStateFront,
      rearTyreWidth: datas.wsDatas.rearTyreWidth,
      rearTyreHeight: datas.wsDatas.rearTyreHeight,
      rearTyreSpeedIndex: datas.wsDatas.rearTyreSpeedIndex,
      rearTyreDiameter: datas.wsDatas.rearTyreDiameter,
      rearTyreWear: datas.tiresStateBack,
      internalColorCode: datas.colors.interior.code,
      internalColorWording: datas.colors.interior.wording,
      internalColorReference: datas.colors.interior.reference,
      externalColorCode: datas.colors.exterior.code,
      externalColorWording: datas.colors.exterior.wording,
      externalColorReference: datas.colors.exterior.reference,
      putIntoService: datas.dateOfRegistration,
      federalMotorTransportAuthorityNumber: datas.wsDatas.federalMotorTransportAuthorityNumber,
      origin: datas.origin,
      purchaseSource: datas.purchaseSource,
      mileage: datas.mileage,
      mileageDate: moment(),
      guaranteedMileage: false,
      forecastedDate:
        datas.stockEntranceMaximumDays !== null
          ? moment().add(datas.stockEntranceMaximumDays, 'd')
          : moment().add(DEFAULT_STOCK_ENTRY_DELAY, 'd'),
      forecastedMileage: Number(
        calculateForecastedMileage(
          datas.mileage,
          datas.stockEntranceMaximumDays,
          datas.dateOfRegistration
        )
      ),
      numberplate: datas.wsDatas.numberplate,
      chassis: datas.wsDatas.chassis,
      registration: datas.wsDatas.registration,
      gasTankCapacity: datas.wsDatas.gasTankCapacity,
      gasTankCapacityUnit: datas.wsDatas.gasTankCapacityUnit,
      autonomy: datas.wsDatas.autonomy,
      wltpAutonomyMin: datas.wsDatas.wltpAutonomyMin,
      wltpAutonomyMax: datas.wsDatas.wltpAutonomyMax,
      batteryCapacityAh: datas.wsDatas.batteryCapacityAh,
      batteryCapacityKwh: datas.wsDatas.batteryCapacityKwh,
      batteryType: datas.wsDatas.batteryType,
      batteryTypeId: datas.wsDatas.batteryTypeId,
      fastBatteryChargingTime: datas.wsDatas.fastBatteryChargingTime,
      gasConsumptionUnit: datas.wsDatas.gasConsumptionUnit,
      gasUrbanConsumption: datas.wsDatas.gasUrbanConsumption,
      wltpGasUrbanConsumptionMin: datas.wsDatas.wltpGasUrbanConsumptionMin,
      wltpGasUrbanConsumptionMax: datas.wsDatas.wltpGasUrbanConsumptionMax,
      gasExtraUrbanConsumption: datas.wsDatas.gasExtraUrbanConsumption,
      wltpGasExtraUrbanConsumptionMin: datas.wsDatas.wltpGasExtraUrbanConsumptionMin,
      wltpGasExtraUrbanConsumptionMax: datas.wsDatas.wltpGasExtraUrbanConsumptionMax,
      gasMixedConsumption: datas.wsDatas.gasMixedConsumption,
      wltpGasMixedConsumptionMin: datas.wsDatas.wltpGasMixedConsumptionMin,
      wltpGasMixedConsumptionMax: datas.wsDatas.wltpGasMixedConsumptionMax,
      gasType: datas.wsDatas.gasType,
      gasTypeId: datas.wsDatas.gasTypeId,
      homeBatteryChargingTime: datas.wsDatas.homeBatteryChargingTime,
      hybridizationType: datas.wsDatas.hybridizationType,
      hybridizationTypeId: datas.wsDatas.hybridizationTypeId,
      powertrain: datas.wsDatas.powertrain,
      powertrainId: datas.wsDatas.powertrainId,
      genericPowertrain: datas.wsDatas.genericPowertrain,
      genericPowertrainId: datas.wsDatas.genericPowertrainId,
      electricConsumption: datas.wsDatas.electricConsumption,
      wltpElectricConsumptionMin: datas.wsDatas.wltpElectricConsumptionMin,
      wltpElectricConsumptionMax: datas.wsDatas.wltpElectricConsumptionMax,
      numberOfBatteries: datas.wsDatas.numberOfBatteries,
      tcenum: datas.wsDatas.tcenum,
      referentialPicture: datas.wsDatas.picture,
      numberOfOwners: datas.historical.firstHand === 1 ? 1 : null,
      upToDateMaintenanceBook:
        datas.historical.maintained !== 2 ? !!datas.historical.maintained : null,
      import: datas.historical.imported !== 2 ? !!datas.historical.imported : null,
      financing: datas.financing && Number(datas.financing),
      applyMaintenanceCosts: true,
      pictures: datas.pictures || [],
      trailerHitch: datas.trailerHitch,
      wltpMixedConsumptionWithDischargedBatteryMin:
        datas.wsDatas.wltpMixedConsumptionWithDischargedBatteryMin,
      wltpMixedConsumptionWithDischargedBatteryMax:
        datas.wsDatas.wltpMixedConsumptionWithDischargedBatteryMax,
      wltpCo2EmissionWithDischargedBatteryMin:
        datas.wsDatas.wltpCo2EmissionWithDischargedBatteryMin,
      wltpCo2EmissionWithDischargedBatteryMax:
        datas.wsDatas.wltpCo2EmissionWithDischargedBatteryMax,
      wltpCo2EmissionClassMin: datas.wsDatas.wltpCo2EmissionClassMin,
      wltpCo2EmissionClassMax: datas.wsDatas.wltpCo2EmissionClassMax,
      wltpCo2ClassWithDischargedBatteryMin: datas.wsDatas.wltpCo2ClassWithDischargedBatteryMin,
      wltpCo2ClassWithDischargedBatteryMax: datas.wsDatas.wltpCo2ClassWithDischargedBatteryMax,
      referentialPollutionClassification: datas.wsDatas.pollutionClassification
        ? datas.wsDatas.pollutionClassification
        : null,
      saddleries:
        // eslint-disable-next-line no-nested-ternary
        datas.interior && datas.interior.length > 0
          ? datas.interior[0].id === 'default'
            ? [
                {
                  code: null,
                  custom: false,
                  exclVatPrice: null,
                  inclVatPrice: null,
                  leather: false,
                  pack: false,
                  referentialId: ' ',
                  serial: true,
                  tax: null,
                  wording: datas.interior[0].label
                }
              ]
            : datas.interior.map(x => ({
                ...x,
                id: Number(x.id),
                referentialId: x.id,
                referentialCode: x.code,
                code: x.manufacturer,
                wording: x.label
              }))
          : null,
      paintings: !datas.colors.type.id
        ? [
            {
              ...datas.colors.type,
              wording: datas.colors.type.equipment,
              code: null,
              custom: false,
              exclVatPrice: null,
              inclVatPrice: null,
              pack: false,
              referentialId: ' ',
              serial: true,
              tax: null
            }
          ]
        : [
            {
              ...datas.colors.type,
              id: Number(datas.colors.type.id),
              referentialId: datas.colors.type.id,
              referentialCode: datas.colors.type.code,
              wording: datas.colors.type.equipment,
              code: datas.colors.type.manufacturer
            }
          ],
      ...specificByCountry(datas)
    }
  }

  static mapOptions = datas =>
    datas
      ? datas.map(option => ({
          pack: option.pack,
          packElements: option.equipmentPacks
            ? option.equipmentPacks.map(equipement => ({
                code: equipement.code,
                wording: equipement.equipmentPack
              }))
            : [],
          equipmentGroupings: option.carEquipmentGroupings
            ? [...option.carEquipmentGroupings.map(x => x[0])]
            : [],
          wording: option.equipment,
          code: option.manufacturer,
          exclVatPrice: option.exclVatPrice,
          inclVatPrice: option.inclVatPrice,
          tax: option.tax,
          referentialId: option.id,
          referentialCode: option.code,
          serial: option.serial,
          custom: option.custom || false
        }))
      : []

  static mapEstimatedDamages = datas => {
    const estimatedDamages = [
      {
        tax: datas.tax,
        type: DAMAGE_TYPE.MAINTENANCE
      },
      {
        tax: datas.tax,
        type: DAMAGE_TYPE.PNEUMATIC,
        tyreSize: getLargestTyreDiameter(datas.wsDatas),
        frontTyreWear: datas.tiresStateFront
      },
      {
        tax: datas.tax,
        type: DAMAGE_TYPE.PNEUMATIC,
        tyreSize: getLargestTyreDiameter(datas.wsDatas),
        rearTyreWear: datas.tiresStateBack
      }
    ]

    if (datas.secondTyres === 1) {
      estimatedDamages.push(
        {
          tax: datas.tax,
          type: DAMAGE_TYPE.SECOND_PNEUMATIC,
          tyreSize: getLargestTyreDiameter(datas.wsDatas),
          secondFrontTyreWear: datas.secondTiresStateFront
        },
        {
          tax: datas.tax,
          type: DAMAGE_TYPE.SECOND_PNEUMATIC,
          tyreSize: getLargestTyreDiameter(datas.wsDatas),
          secondRearTyreWear: datas.secondTiresStateBack
        }
      )
    }

    if (datas.damages) {
      if (datas.defectsMustGoToManual) {
        estimatedDamages.push(
          ...datas.damages.map(damage => ({
            type: DAMAGE_TYPE.MANUAL,
            wording:
              Number(damage.sectionId) === constants.SECTIONS.OTHER
                ? damage.defectWording
                : `${damage.section} ${damage.defectWording.toLowerCase()}`,
            tax: datas.tax,
            privateExclVatPrice: damage.privatePrice,
            professionalExclVatPrice: damage.professionalPrice,
            pictures: damage.pictures || []
          }))
        )
      } else {
        estimatedDamages.push(
          ...datas.damages.map(damage => ({
            section: damage.sectionId,
            defectId: damage.defectId,
            type: DAMAGE_TYPE.DEFECT,
            tax: datas.tax,
            wording: damage.defectWording,
            privateExclVatPrice: damage.privatePrice,
            professionalExclVatPrice: damage.professionalPrice,
            pictures: damage.pictures || []
          }))
        )
      }
    }

    return estimatedDamages
  }

  static map = (method, datas) => {
    if (method === mapperMethods.MAP_VEHICLE) {
      return Mapper.mapVehicle(datas)
    }
    if (method === mapperMethods.MAP_OPTIONS) {
      return Mapper.mapOptions(datas)
    }
    if (method === mapperMethods.MAP_ESTIMATED_DAMAGES) {
      return Mapper.mapEstimatedDamages(datas)
    }

    return null
  }
}

export default Mapper
